import React, { useState, useEffect, useContext } from "react";
import OwnerLayout from "../../../layout/OwnerLayout";
import { getOwnerDashboardData } from "../../../../api/dashboard.api";
import { FaCar, FaTv } from "react-icons/fa";
import { IoFastFoodOutline } from "react-icons/io5";
import { UserContext, useUser } from "../../../../context/UserContext";

const initialOwnerDashboardStats = [
  {
    title: "Total Mobile Locations",
    value: 0,
    borderColor: "border-blue-600",
    bgGradient: "from-blue-500 to-blue-600",
    shadowColor: "shadow-blue-500/20",
    hoverShadow: "hover:shadow-blue-500/30",
    icon: <FaCar className="text-blue-600 text-5xl" />,
    description: "Fleet management overview",
  },
  {
    title: "Total Screens",
    value: 0,
    borderColor: "border-green-600",
    bgGradient: "from-green-500 to-green-600",
    shadowColor: "shadow-green-500/20",
    hoverShadow: "hover:shadow-green-500/30",
    icon: <FaTv className="text-green-600 text-5xl" />,
    description: "Active display units",
  },
  {
    title: "Total Fixed Locations",
    value: 0,
    borderColor: "border-red-800",
    bgGradient: "from-red-700 to-red-800",
    shadowColor: "shadow-red-500/20",
    hoverShadow: "hover:shadow-red-500/30",
    icon: <IoFastFoodOutline className="text-red-800 text-5xl" />,
    description: "Partner establishments",
  },
];

// Loading skeleton component
const StatCardSkeleton = () => (
  <div className="bg-white rounded-2xl p-8 shadow-lg animate-pulse">
    <div className="flex items-center justify-between">
      <div className="space-y-3">
        <div className="h-4 bg-gray-300 rounded w-32"></div>
        <div className="h-8 bg-gray-300 rounded w-16"></div>
        <div className="h-3 bg-gray-300 rounded w-24"></div>
      </div>
      <div className="w-16 h-16 bg-gray-300 rounded-full"></div>
    </div>
  </div>
);

// Animated counter component
const AnimatedCounter = ({ value, duration = 2000 }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime = null;
    const animateCount = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);

      setCount(Math.floor(progress * value));

      if (progress < 1) {
        requestAnimationFrame(animateCount);
      }
    };

    requestAnimationFrame(animateCount);
  }, [value, duration]);

  return <span>{count}</span>;
};

// Enhanced stat card component
const StatCard = ({ stat, index, isLoading }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className={`
        relative overflow-hidden bg-white rounded-2xl shadow-lg ${stat.shadowColor} 
        border-l-[6px] ${stat.borderColor} p-8 
        transform transition-all duration-300 ease-out hover:scale-105 
        ${stat.hoverShadow} hover:shadow-2xl
        lg:col-span-6 md:col-span-12 col-span-12
        group cursor-pointer
      `}
      style={{
        animationDelay: `${index * 150}ms`,
        animation: isLoading ? "none" : "slideInUp 0.6s ease-out forwards",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Animated background gradient */}
      <div
        className={`
          absolute inset-0 bg-gradient-to-r ${stat.bgGradient} opacity-0 
          transition-opacity duration-300 ${isHovered ? "opacity-5" : ""}
        `}
      />

      {/* Floating particles effect */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            className={`
              absolute w-1 h-1 bg-gradient-to-r ${stat.bgGradient} rounded-full opacity-30
              animate-ping
            `}
            style={{
              left: `${20 + i * 30}%`,
              top: `${30 + i * 20}%`,
              animationDelay: `${i * 0.5}s`,
              animationDuration: "2s",
            }}
          />
        ))}
      </div>

      <div className="relative z-10 flex items-center justify-between">
        <div className="space-y-2">
          <h3 className="text-gray-500 font-medium text-lg tracking-wide">
            {stat.title}
          </h3>
          <div className="text-4xl font-bold text-gray-800 flex items-center">
            <AnimatedCounter value={stat.value} />
            <span className="text-2xl ml-2 text-gray-400">
              {stat.value > 0 && "+"}
            </span>
          </div>
          <p className="text-sm text-gray-400 font-medium opacity-80">
            {stat.description}
          </p>
        </div>

        {/* Animated icon container */}
        <div
          className={`
            relative p-4 rounded-full bg-gradient-to-r ${stat.bgGradient} 
            transition-all duration-300 transform
            ${isHovered ? "scale-110 rotate-12" : "scale-100 rotate-0"}
          `}
        >
          <div className="text-white text-4xl">
            {React.cloneElement(stat.icon, {
              className:
                "text-white text-4xl transition-transform duration-300",
            })}
          </div>

          {/* Pulse ring effect */}
          <div
            className={`
              absolute inset-0 rounded-full bg-gradient-to-r ${stat.bgGradient} 
              animate-ping opacity-20
              ${isHovered ? "block" : "hidden"}
            `}
          />
        </div>
      </div>

      {/* Bottom accent line */}
      <div
        className={`
          absolute bottom-0 left-0 h-1 bg-gradient-to-r ${stat.bgGradient}
          transition-all duration-500 ease-out
          ${isHovered ? "w-full" : "w-0"}
        `}
      />
    </div>
  );
};

function OwnerDashboard() {
  const { user } = useContext(UserContext);
  const [ownerDashboardStats, setOwnerDashboardStats] = useState(
    initialOwnerDashboardStats
  );
  const [loading, setLoading] = useState(true);
  const { currentView } = useUser();
  useEffect(() => {
    // Fetch dashboard data only (skip fetching user details)
    getOwnerDashboardData()
      .then((res) => {
        const data = res?.data?.data;
        if (data) {
          setOwnerDashboardStats([
            {
              ...initialOwnerDashboardStats[0],
              value: data.totalVehicles || 0,
            },
            { ...initialOwnerDashboardStats[1], value: data.totalScreens || 0 },
            {
              ...initialOwnerDashboardStats[2],
              value: data.totalRestaurants || 0,
            },
          ]);
        }
      })
      .catch((error) => {
        console.error("Error fetching owner dashboard data:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  return (
    <OwnerLayout
      ownerDetails={user}
      title={`Owner Dashboard - ${
        currentView === "company" ? "Company View" : "Owner View"
      }`}
    >
      <div className="p-10 min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Enhanced header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-2">
            <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-purple-600 rounded-full"></div>
            <h1 className="text-3xl font-bold text-gray-800 tracking-tight">
              Dashboard Overview
            </h1>
          </div>
          <p className="text-gray-600 ml-6">
            Welcome back! Here's what's happening with your business today.
          </p>
        </div>

        {loading ? (
          <div className="grid gap-6 grid-cols-12">
            {[...Array(3)].map((_, index) => (
              <div
                key={index}
                className="lg:col-span-6 md:col-span-12 col-span-12"
              >
                <StatCardSkeleton />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid gap-6 grid-cols-12">
            {ownerDashboardStats.map((stats, index) => (
              <StatCard
                key={index}
                stat={stats}
                index={index}
                isLoading={loading}
              />
            ))}
          </div>
        )}

        {/* Additional dashboard summary */}
        {!loading && (
          <div className="mt-12 bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Quick Stats Summary
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {ownerDashboardStats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div
                    className={`inline-flex p-3 rounded-full bg-gradient-to-r ${stat.bgGradient} mb-3`}
                  >
                    {React.cloneElement(stat.icon, {
                      className: "text-white text-2xl",
                    })}
                  </div>
                  <p className="text-sm text-gray-600">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-800">
                    {stat.value}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
      `}</style>
    </OwnerLayout>
  );
}

export default OwnerDashboard;
